import React, { useMemo, useState } from 'react';
import { Check } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { type ApolloQueryResult } from '@apollo/react-hooks';
import { type RatingForm } from '../types';
import { getDepartmentRating } from '../panelDataHandling';
import { ApplicationReviewDialog } from '../ApplicationReviewDialog';
import type {
  PanelDimensionalScores,
  Reviewers,
} from '../../../../../graphql/GSAssessment';
import { ROLES_MODULE } from '../../../../../../../components/RoleSwitchBar/roles';
import {
  DIMENSION_TYPE,
  type FundingSource,
} from '../../../../../../Nomination/components';
import { PANEL_DETAIL_APPLICATION_TEXT } from '../../../../../lang';

type PanelReviewersProps = {
  reviewers: Reviewers[];
  applicantName: string;
  applicationNo: string;
  applicationId: string;
  triggerRender: number;
  panelId: string;
  scholarshipScore: number;
  panelDimensionalScores: PanelDimensionalScores[];
  currentRole: string | null;
  currentNetId: string;
  fundingSourceEnum: string;
  refetch: () => Promise<ApolloQueryResult<any>>;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const PanelReviewers: React.FunctionComponent<PanelReviewersProps> = (
  props: PanelReviewersProps,
) => {
  const {
    reviewers,
    triggerRender,
    applicantName,
    applicationId,
    currentRole,
    panelId,
    currentNetId,
    applicationNo,
    fundingSourceEnum,
    panelDimensionalScores,
    scholarshipScore,
    refetch,
    handleLoading,
  } = props;

  const [openApplicationReviewDialog, setOpenApplicationReviewDialog] =
    useState<boolean>(false);

  const currentReviewerAmendedScores = reviewers?.find(
    item => item.netId === currentNetId, // only current user as the reviewer can amend scores
  )?.scoresOverride;

  const returnDetailedScores = (dimensionType: DIMENSION_TYPE): number => {
    const matchedScores = panelDimensionalScores?.find(
      dm => dm.dimension === dimensionType,
    );
    return getDepartmentRating(
      matchedScores?.systemScore,
      matchedScores?.overrideScore,
    );
  };

  const MemorizedApplicationReviewDialog = useMemo((): React.ReactNode => {
    const dimensionScores = {
      AE: returnDetailedScores(DIMENSION_TYPE.AE),
      RP: returnDetailedScores(DIMENSION_TYPE.RP),
      LS: returnDetailedScores(DIMENSION_TYPE.LS),
      CI: returnDetailedScores(DIMENSION_TYPE.CI),
      RE: returnDetailedScores(DIMENSION_TYPE.RE),
      OS: returnDetailedScores(DIMENSION_TYPE.OS),
      CD: returnDetailedScores(DIMENSION_TYPE.CD),
      US: returnDetailedScores(DIMENSION_TYPE.US),
    };

    const amendedScoresData: RatingForm = {
      totalScore: currentReviewerAmendedScores?.totalScore ?? '',
      AE: currentReviewerAmendedScores?.AE ?? '',
      AEComment: currentReviewerAmendedScores?.AEComment ?? '',
      RP: currentReviewerAmendedScores?.RP ?? '',
      RPComment: currentReviewerAmendedScores?.RPComment ?? '',
      LS: currentReviewerAmendedScores?.LS ?? '',
      LSComment: currentReviewerAmendedScores?.LSComment ?? '',
      CI: currentReviewerAmendedScores?.CI ?? '',
      CIComment: currentReviewerAmendedScores?.CIComment ?? '',
      RE: currentReviewerAmendedScores?.RE ?? '',
      REComment: currentReviewerAmendedScores?.REComment ?? '',
      OS: currentReviewerAmendedScores?.OS ?? '',
      OSComment: currentReviewerAmendedScores?.OSComment ?? '',
      CD: currentReviewerAmendedScores?.CD ?? '',
      CDComment: currentReviewerAmendedScores?.CDComment ?? '',
      US: currentReviewerAmendedScores?.US ?? '',
      USComment: currentReviewerAmendedScores?.USComment ?? '',
    };
    return (
      <>
        {openApplicationReviewDialog && (
          <ApplicationReviewDialog
            openDialog={openApplicationReviewDialog}
            dialogContent={{
              applicantName,
              applicationNo,
              fundingSource: fundingSourceEnum as FundingSource,
              panelId,
              applicationId,
              amendedScoresData,
              scores: dimensionScores,
              refetch,
              handleLoading,
            }}
            onDialogClose={() => {
              setOpenApplicationReviewDialog(false);
            }}
          />
        )}
      </>
    );
  }, [openApplicationReviewDialog]);

  const handleOnClickReview = (): void => {
    setOpenApplicationReviewDialog(true);
  };
  return (
    <>
      <div className="flex flex-col gap-2" key={triggerRender}>
        {reviewers.map((reviewer, index) => {
          const { netId, name, department, classification, scoresOverride } =
            reviewer;
          const reviewerInfo = `${name} [${department}] (${classification})`;
          const isAllowOpenReviewDetails = netId === currentNetId;
          const isPanelMember = currentRole === ROLES_MODULE.RIGHT_PANEL;
          const isClickable = isAllowOpenReviewDetails && isPanelMember;
          const hasAmendedScores = Object.values(reviewer.scoresOverride).some(
            value => Boolean(value),
          );

          return (
            <Tooltip key={index} title={reviewerInfo}>
              <span
                className="flex flex-row items-center gap-1 cursor-pointer justify-start"
                onClick={isClickable ? handleOnClickReview : undefined}
              >
                <Check
                  sx={{
                    color: hasAmendedScores
                      ? isAllowOpenReviewDetails
                        ? '#951D1D'
                        : '#000000'
                      : 'transparent',
                    fontSize: '1rem',
                  }}
                />
                <p
                  className={`w-2/3 overflow-hidden text-ellipsis whitespace-nowrap ${isClickable && 'text-[#951D1D] underline'}`}
                >
                  {reviewerInfo}
                </p>
                &nbsp;
                <b className={`${isClickable && 'text-[#951D1D] underline'}`}>
                  {scoresOverride?.totalScore ?? 0}
                </b>
              </span>
            </Tooltip>
          );
        })}
        <span className="flex flex-row items-center gap-1 w-full justify-start">
          <p className="w-[1rem]" />
          <p className="w-2/3 overflow-hidden text-ellipsis">
            {PANEL_DETAIL_APPLICATION_TEXT.TOTAL_SCORE}
          </p>
          &nbsp;
          <b>{scholarshipScore}</b>
        </span>
      </div>
      {MemorizedApplicationReviewDialog}
    </>
  );
};
const MemoizedPanelReviewers = React.memo(PanelReviewers);
export { MemoizedPanelReviewers };
